import { getElementNaturalHeight, getBreakpointIndex } from "../utils";
import focusLock from "dom-focus-lock";

const SUBMENU_SELECTOR = ".submenu";

/**
 * animateIn
 * @param {*} submenu
 */
function animateIn(submenu, onComplete, parentElement, chevron) {
  let timeline = new gsap.timeline({ paused: true });

  let currentDimensions = submenu.getBoundingClientRect();
  let newHeight = getElementNaturalHeight(submenu);

  timeline.fromTo(
    submenu,
    {
      opacity: 0,
      height: currentDimensions.height,
    },
    {
      height: newHeight,
      opacity: 1,
      duration: 0.25,
      ease: "Power0.easeNone",
      onComplete: onComplete,
    },
    0
  );

  timeline.to(
    chevron,
    {
      rotation: "180_short",
      duration: 0.25,
      ease: "Power0.easeNone",
    },
    0
  );

  timeline.play();

  return timeline;
}

/**
 * animateOut
 * @param {*} submenu
 */
function animateOut(submenu, chevron, onComplete) {
  let timeline = new gsap.timeline({ paused: true });
  timeline.to(
    submenu,
    {
      height: "0px",
      opacity: 0,
      onComplete: onComplete,
    },
    0
  );
  timeline.to(
    chevron,
    {
      rotate: "0_short",
      duration: 0.25,
      ease: "Power0.easeNone",
    },
    0
  );
  timeline.play();
  return timeline;
}

/**
 * Submenu
 * @param {*} element
 * @param {*} closeAll
 */
export const Submenu = (element) => {
  // init
  let state = {
    active: false,
    transitioning: false,
  };
  let submenu = element.querySelector(SUBMENU_SELECTOR);
  let timeline = new gsap.timeline({ paused: true });
  let button = element.querySelector(".submenu-toggle");
  let chevron = button.querySelector(".submenu-toggle--image");

  const eventOpen = document.createEvent("Event");
  eventOpen.initEvent("submenu_open", true, true);

  const eventClose = document.createEvent("Event");
  eventClose.initEvent("submenu_close", true, true);

  document.addEventListener("menu_transition", (event) => {
    if (getBreakpointIndex() >= 3) {
      element.addEventListener("mouseenter", open);
      element.addEventListener("mouseleave", close);
    } else {
      element.removeEventListener("mouseenter", open);
      element.removeEventListener("mouseleave", close);
    }
    close();
  });

  // function to handle escape key being pressed when submenu is open
  const handleEscapeKeyPress = (event) => {
    if (event.isComposing || event.keyCode === 27) {
      if (state.active) {
        close();
      }
    }
  };

  /**
   * open
   */
  const open = (event) => {
    if (event && event.type === "mouseup" && state.transitioning) {
      return;
    }

    submenu.dispatchEvent(eventOpen);

    state.transitioning = true;

    submenu.querySelectorAll("a").forEach((link) => {
      link.setAttribute("tabindex", "0");
    });

    if (event && event.type === "keydown") {
      focusLock.on(submenu);
    }

    timeline = animateIn(
      submenu,
      () => {
        state.active = true;
        state.transitioning = false;
      },
      element,
      chevron
    );

    window.addEventListener("keydown", handleEscapeKeyPress);
  };

  /**
   * close
   */
  const close = (event) => {
    if (event && event.type === "mouseup" && state.transitioning) {
      return;
    }

    state.transitioning = true;

    submenu.querySelectorAll("a").forEach((link) => {
      link.setAttribute("tabindex", "-1");
    });

    focusLock.off(submenu);

    submenu.dispatchEvent(eventClose);
    timeline = animateOut(submenu, chevron, () => {
      state.active = false;
      state.transitioning = false;
    });

    window.removeEventListener("keydown", handleEscapeKeyPress);
  };

  // setup accessibility enhancements
  submenu.querySelectorAll("a").forEach((link) => {
    link.setAttribute("tabindex", "-1");
  });

  //   Event bindings
  if (getBreakpointIndex() >= 3) {
    element.addEventListener("mouseenter", open);
    element.addEventListener("mouseleave", close);
  }

  button.addEventListener("keydown", (event) => {
    if (event.keyCode === 32 || event.keyCode === 13) {
      state.active === false ? open(event) : close(event);
    }
  });

  button.addEventListener("mouseup", (event) => {
    state.active === false ? open(event) : close(event);
  });
};
